import { useCallback, useMemo, useState } from 'react';
import { useGetEventRegistriesAndOrdersQuery } from '@graphql/generated';
import { useDisclosure } from '@withjoy/joykit';
import { CookedProduct, useCookedProductList } from '@apps/registry/common/selectors/ProductListSelector';

export const useRegistryItemProductStatus = ({ eventHandle }: { eventHandle?: string | null }) => {
  const { isOpen: isOpenEditGiftDialog, onClose: onCloseEditGift, onOpen: onOpenEditGift } = useDisclosure();
  const [editSelectedProduct, setEditSelectedProduct] = useState<CookedProduct | null>(null);
  const { data } = useGetEventRegistriesAndOrdersQuery({
    batchMode: 'fast',
    skip: !eventHandle,
    variables: { eventHandle: eventHandle! }
  });
  const orders = data?.eventByName?.registry.orders;
  const registries = data?.eventByName?.info.registry;

  const cookedProductList = useCookedProductList(registries, orders, 'everything');

  const { products: productList } = cookedProductList;

  const productMap: Map<string, CookedProduct> = useMemo(() => {
    // Create a map of product IDs to CookedProduct objects
    const map = new Map<string, CookedProduct>();

    productList?.forEach(product => {
      const pdpUrl = product.pdpUrl;
      if (!pdpUrl) return;

      const match = pdpUrl.match(/shop\/item\/([^/]+)/);
      if (match) {
        const productId = match[1];
        map.set(productId, product);
      }
    });

    return map;
  }, [productList]);

  const onOpenEditGiftDialog = useCallback(
    (productId: string) => {
      const currentProduct = productMap.get(productId) ?? null;
      setEditSelectedProduct(currentProduct);
      onOpenEditGift();
    },
    [onOpenEditGift, productMap]
  );
  const onCloseEditGiftDialog = useCallback(() => {
    setEditSelectedProduct(null);
    onCloseEditGift();
  }, [onCloseEditGift]);

  return {
    isProductInRegistry: (productId: string) => productMap.has(productId),
    onOpenEditGiftDialog,
    onCloseEditGiftDialog,
    editSelectedProduct,
    isOpenEditGiftDialog,
    currentCookedProduct: editSelectedProduct
  };
};
